import { useCallback, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { DownloadLink } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button, IconButton } from 'ui/components/Buttons'
import { IconName } from 'ui/components/Icon'
import { Page, PageFace } from 'ui/components/Page'
import { Toast } from 'ui/components/Toast'
import { useConfirmation } from 'ui/hooks'
import { SARSideBar } from '~bank-bsa-reporting/components'
import { useGetSARDetails } from '~bank-bsa-reporting/hooks/sar'

import { handlerServerError } from '@orbit/errors'

import {
  EditPeriodDialog,
  FilingInstitutionContactInformation,
  FinancialInstitutionWhereActivityOccurred,
  Footer,
  GeneralInformation,
  History,
  Narrative,
  SubjectInformation,
  SuspiciousActivityInformation,
  TransactionsInformation,
  Warnings
} from './components'

const routeApi = getRouteApi('/_app/bsa/sars/$id')

const SARDetails = () => {
  const navigate = routeApi.useNavigate()
  const params = routeApi.useParams()
  const queryClient = useQueryClient()

  const { data, isFetching, onHistoryFilterChange } = useGetSARDetails(+(params.id || 0))
  const { id = 0, generalInfo } = data || {}
  const { transactionStartDate = '', transactionEndDate = '' } = generalInfo || {}

  const { mutateAsync: deleteSAR } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.delete(Request.urls.bank.sar, opts),
    onError: (error) => handlerServerError(error),
    onSuccess: () => {
      Toast.successSmall(`SAR #${id} was successfully deleted.`)
      navigate({ to: routes.bank.bsa.sar.index })
    }
  })

  const { mutate: acknowledgeWarnings } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.sarAcknowledgeWarnings, opts),
    onError: ({ message }: { message: string }) => {
      Toast.errorSmall(message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sar-details'] })
    }
  })

  const { open: startDeleting, Confirmation } = useConfirmation({
    message: `Are you sure you want to delete #${id} SAR?`,
    onConfirm: () => deleteSAR({ id }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const resolveWarnings = useCallback(() => {
    acknowledgeWarnings({ id })
  }, [id])

  const [openEditPeriodDialog, setOpenEditPeriodDialog] = useState(false)
  const handleError = (message: string) => Toast.error('Error', message)

  return (
    <Page
      face={PageFace.SECONDARY}
      title={id ? `SAR #${id}` : '...'}
      subTitle="SAR"
      isPending={isFetching && !data}
      actions={
        <ButtonGroup margin="big">
          <IconButton onClick={startDeleting} icon={IconName.DELETE} face="neutral" data-qa="Delete SAR">
            Delete SAR
          </IconButton>
          <DownloadLink
            direct
            mimeType="application/vnd.ms-excel"
            name={`Transactions_${id}`}
            parametersForm={{
              sar_id: id
            }}
            baseUrl={Request.urls.bank.bsaSarTransactionsXls}
            handleError={handleError}
            withIcon={false}
          >
            <Button face="inherit white">Download Transactions</Button>
          </DownloadLink>
        </ButtonGroup>
      }
      footer={<Footer data={data} />}
    >
      <Confirmation />
      {openEditPeriodDialog && (
        <EditPeriodDialog
          id={id}
          initialValue={{
            startDate: new Date(transactionStartDate),
            endDate: new Date(transactionEndDate)
          }}
          onClose={() => setOpenEditPeriodDialog(false)}
        />
      )}
      {!!data && (
        <>
          <SARSideBar />
          <Warnings onResolve={resolveWarnings} data={data} />
          <GeneralInformation openEditPeriodDialog={() => setOpenEditPeriodDialog(true)} data={data} />
          <FilingInstitutionContactInformation data={data} />
          <FinancialInstitutionWhereActivityOccurred data={data} />
          <SubjectInformation data={data} />
          <SuspiciousActivityInformation data={data} />
          <TransactionsInformation data={data} />
          <Narrative data={data} />
          <History data={data} onFilterChange={onHistoryFilterChange} isFetching={isFetching} />
        </>
      )}
    </Page>
  )
}

export default SARDetails
